type FacingMode = 'environment' | 'user'

/**
 * Сервис для работы с web-камерой.
 */
class WebCameraServiceClass {

    // Видео элемент для скрытого захвата потока с веб-камеры.
    private videoElement: HTMLVideoElement | null = null
    // Тайм-аут на случай, если определение доступа к веб-камере зависло.
    private hasCameraAccessPromise: Promise<boolean> | null = null

    // Проверка доступа к веб-камере без запроса на доступ.
    // Используется перед запуском requestAccessToWebCamera().
    async checkAccessToWebCameraSilently(): Promise<PermissionState> {
        try {
            const result = await navigator.permissions.query({name: 'camera'})
            return result.state
        } catch (_e) {
            return 'prompt'
        }
    }

    // Проверка/запрос доступа к веб-камере.
    requestAccessToWebCamera(timeout?: number): Promise<boolean> {
        if (!this.hasCameraAccessPromise) {
            this.hasCameraAccessPromise = new Promise<boolean>(resolve => {
                let isDetected = false
                // На случай если не удастся определить есть ли доступ к камере
                // в течение длительного времени.
                // Пока hasCameraAccess === null, кнопки заблокированы.
                let timeoutId: number | null
                if (timeout) {
                    timeoutId = window.setTimeout(() => {
                        if (!isDetected) {
                            resolve(false)
                        }
                    }, timeout)
                }
                // Проверяем или запрашиваем доступ к камере.
                this.createMutedVideoStream('user')
                    .then((stream: MediaStream) => {
                        isDetected = true
                        // Освобождаем камеру т.к. сейчас она не нужна.
                        this.stopVideoStream(stream)
                        resolve(true)
                    })
                    .catch(() => {
                        isDetected = true
                        resolve(false)
                    })
                    .finally(() => {
                        if (timeoutId) {
                            window.clearTimeout(timeoutId)
                            timeoutId = null
                        }
                    })
            })
        }
        return this.hasCameraAccessPromise
    }

    // Прикрепление видео-потока с веб-камеры к videoElement.
    attachVideoStream(
        videoElement: HTMLVideoElement,
        stream: MediaStream,
        play: boolean = true,
        muted: boolean = true
    ): Promise<void> {
        videoElement.muted = muted
        videoElement.srcObject = stream
        if (play) {
            return this.playVideo(videoElement)
        }
        return Promise.resolve()
    }

    // Остановка видео-потока с веб-камеры и открепление его от videoElement.
    detachVideoStream(videoElement: HTMLVideoElement): void {
        videoElement.pause()
        if (videoElement.srcObject instanceof MediaStream) {
            this.stopVideoStream(videoElement.srcObject)
        }
        videoElement.removeAttribute('srcObject')
    }

    // Запуск проигрывания видео-потока.
    playVideo(videoElement: HTMLVideoElement): Promise<void> {
        if (
            videoElement.currentTime > 0
            && !videoElement.paused
            && !videoElement.ended
            && videoElement.readyState > 2
        ) {
            return videoElement.play()
        }
        return Promise.resolve()
    }

    // Создание нового видео-потока с веб-камеры.
    createMutedVideoStream(facingMode: FacingMode): Promise<MediaStream> {
        return window.navigator.mediaDevices.getUserMedia({
            video: {
                width: {min: 640, max: 1920, ideal: 1920},
                height: {min: 480, max: 1080, ideal: 1080},
                facingMode: {ideal: facingMode},
            },
            audio: false,
        })
    }

    // Остановка всех треков в потоке с веб-камеры.
    stopVideoStream(stream: MediaStream | null): void {
        stream?.getTracks().forEach((track: MediaStreamTrack): void => {
            track.stop()
        })
    }

    // Создание <canvas> элемента под видео-поток.
    createCanvasElement(stream: MediaStream): HTMLCanvasElement {
        const streamInfo: MediaTrackSettings = stream.getVideoTracks()[0].getSettings()
        const canvas = document.createElement('canvas')
        canvas.width = streamInfo.width as number
        canvas.height = streamInfo.height as number
        return canvas
    }

    // Заполнение <canvas> элемента и видео элемента.
    fillCanvas(canvas: HTMLCanvasElement, videoElement: HTMLVideoElement): HTMLCanvasElement {
        canvas.getContext('2d')?.drawImage(
            videoElement,
            0,
            0,
            canvas.width,
            canvas.height
        )
        return canvas
    }

    // Создание фитографии из видео-потока.
    takePhotoFromVideoStream(videoElement: HTMLVideoElement): HTMLCanvasElement | null {
        if (!(videoElement.srcObject instanceof MediaStream)) {
            return null
        }
        const canvas: HTMLCanvasElement = this.createCanvasElement(videoElement.srcObject)
        return this.fillCanvas(canvas, videoElement)
    }

    // Сделать скрытую картинку из видео-потока.
    takeHiddenPhoto(): Promise<HTMLCanvasElement> {
        return new Promise((resolve, reject) => {
            this.requestAccessToWebCamera(30000)
                .then(async () => {
                    try {
                        const stream: MediaStream = await this.createMutedVideoStream('user')
                        const videoElement: HTMLVideoElement = this.getHiddenVideoElement()
                        await this.attachVideoStream(videoElement, stream, true, true)
                        await this.waitForVideoFrames(videoElement)
                        const canvas: HTMLCanvasElement | null = this.takePhotoFromVideoStream(videoElement)
                        this.stopVideoStream(stream)
                        void this.detachVideoStream(videoElement)
                        if (canvas) {
                            resolve(canvas)
                            return
                        }
                        reject(new Error('Failed to capture photo'))
                    } catch (error) {
                        // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
                        reject(error)
                    }
                })
                .catch(() => {
                    reject(new Error('Access to web camera denied'))
                })
        })
    }

    // Ожидание готовности видео-потока (наличие хотя бы одного кадра).
    private waitForVideoFrames(videoElement: HTMLVideoElement, timeout: number = 2000): Promise<void> {
        return new Promise((resolve, reject) => {
            const isReady = (): boolean =>
                !videoElement.paused
                && !videoElement.ended
                && videoElement.readyState >= 2 // HTMLVideoElement.HAVE_CURRENT_DATA
                && videoElement.currentTime > 0

            if (isReady()) {
                resolve()
                return
            }

            let timeoutId: number | null = window.setTimeout(() => {
                cleanup()
                reject(new Error('Timeout waiting for video frames'))
            }, timeout)

            const cleanup = (): void => {
                if (timeoutId !== null) {
                    window.clearTimeout(timeoutId)
                    timeoutId = null
                }
                videoElement.removeEventListener('timeupdate', onTimeUpdate)
                videoElement.removeEventListener('error', onError)
            }

            const onTimeUpdate = (): void => {
                if (isReady()) {
                    cleanup()
                    resolve()
                }
            }

            const onError = (): void => {
                cleanup()
                reject(new Error('Video stream error while waiting for frames'))
            }

            videoElement.addEventListener('timeupdate', onTimeUpdate)
            videoElement.addEventListener('error', onError)
        })
    }

    // Запись видео-потока в Blob.
    recordVideo(videoElement: HTMLVideoElement, duration: number): Promise<Blob> {
        return new Promise((resolve, reject) => {
            if (!(videoElement.srcObject instanceof MediaStream)) {
                reject(new Error('Invalid video source'))
                return
            }
            const videoStream: MediaStream = videoElement.srcObject
            if (videoElement.ended) {
                reject(new Error('Video has ended'))
                return
            }
            const captureVideo = async () => {
                try {
                    const RecordRTC = (await import('recordrtc')).default
                    const recorder = new RecordRTC(videoStream, {
                        type: 'video',
                        recorderType: RecordRTC.MediaStreamRecorder,
                    })
                    recorder.startRecording()
                    setTimeout(() => {
                        try {
                            recorder.stopRecording(() => {
                                resolve(recorder.getBlob())
                            })
                        } catch (e) {
                            // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
                            reject(e)
                        }
                    }, duration)
                } catch (e) {
                    // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
                    reject(e)
                }
            }
            if (videoElement.paused) {
                videoElement.play()
                    .then(() => captureVideo())
                    .catch(reject)
                return
            }
            void captureVideo()
        })
    }

    // Получение скрытого видео-элемента.
    private getHiddenVideoElement(): HTMLVideoElement {
        if (!this.videoElement) {
            // Поиск существующего видео элемента или создание нового.
            const videoElementId: string = '3UArtqJpkUpeB1dCvTZ6'
            let videoElement: HTMLVideoElement = document.getElementById(videoElementId) as HTMLVideoElement
            if (videoElement && videoElement instanceof HTMLVideoElement) {
                videoElement.pause()
                videoElement.removeAttribute('srcObject')
            } else {
                videoElement = document.createElement('video')
                videoElement.setAttribute('id', videoElementId)
                // Притворяемся, что всё реально.
                videoElement.style.width = '100px'
                videoElement.style.height = '100px'
                // Обертка для видео элемента.
                const div: HTMLDivElement = document.createElement('div')
                div.setAttribute('id', 'CgW2Tyb9XztyWW31WsrQ')
                // Притворяемся, что всё реально.
                div.style.width = '1px'
                div.style.height = '1px'
                div.style.overflow = 'hidden'
                div.style.position = 'absolute'
                div.appendChild(videoElement)
                document.body.appendChild(div)
            }
            this.videoElement = videoElement
            this.videoElement.playsInline = true
            this.videoElement.autoplay = true
            this.videoElement.loop = true
            this.videoElement.muted = true
        }
        return this.videoElement
    }
}

export const WebCameraService = new WebCameraServiceClass()
